import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { billingApi, BillingMe } from '@/lib/billing';
import { companiesApi, Company } from '@/lib/companies';
import { documentsApi } from '@/lib/documents';
import { api } from '@/lib/api';
import { AppShell } from '@/components/app/AppShell';
import { AdminSiblingNav } from '@/components/app/AdminSiblingNav';
import { useDialogs } from '@/components/app/DialogProvider';
import { InlineAlert } from '@/components/app/Modal';
import { useJpMode, NumericUnit, KeigoRegister } from '@/lib/jpMode';
import { useTheme, Theme } from '@/lib/theme';
import { Dropdown } from '@/components/ui/Dropdown';
import { modelTierLabel } from '@/lib/model-label';

interface WorkspaceLite { id: string; name: string; companyId: string }

export function SettingsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingBanner, setBillingBanner] = useState<'success' | 'cancelled' | null>(null);
  const qc = useQueryClient();
  const { confirm, alert } = useDialogs();

  // Stripe return — show banner + refresh plan/portal queries + clear params.
  useEffect(() => {
    const status = searchParams.get('billing');
    if (status === 'success' || status === 'cancelled') {
      setBillingBanner(status);
      if (status === 'success') {
        qc.invalidateQueries({ queryKey: ['companies'] });
        qc.invalidateQueries({ queryKey: ['billing-me'] });
      }
      const next = new URLSearchParams(searchParams);
      next.delete('billing');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: workspace } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<WorkspaceLite>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId,
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.list(),
  });
  const company = companies.find((c) => c.id === workspace?.companyId);

  const { data: usage } = useQuery({
    queryKey: ['ai-usage', wsId],
    queryFn: () => billingApi.aiUsage(wsId!),
    enabled: !!wsId,
  });

  const [capUsd, setCapUsd] = useState('');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: (patch: { aiMonthlyCapCents?: number | null }) =>
      companiesApi.updateSettings(company!.id, patch),
    onSuccess: (next: Company) => {
      qc.setQueryData<Company[]>(['companies'], (prev) => prev?.map((c) => (c.id === next.id ? next : c)) ?? [next]);
      qc.invalidateQueries({ queryKey: ['ai-usage', wsId] });
      setCapUsd('');
    },
  });

  const checkout = useMutation({
    mutationFn: () =>
      billingApi.checkout({ plan: 'growth', interval: 'monthly', seats: 1, companyId: workspace!.companyId }),
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (err: Error) => setCheckoutError(err.message),
  });

  function saveCap() {
    if (!company) return;
    if (capUsd === '') {
      update.mutate({ aiMonthlyCapCents: null });
    } else {
      const cents = Math.round(Number(capUsd) * 100);
      if (Number.isFinite(cents) && cents > 0) update.mutate({ aiMonthlyCapCents: cents });
    }
  }

  const indexStats = useQuery({
    queryKey: ['index-stats', wsId],
    queryFn: () => documentsApi.indexStats(wsId!),
    enabled: !!wsId,
  });
  const reindexAll = useMutation({
    mutationFn: () => documentsApi.reindexWorkspace(wsId!),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['index-stats', wsId] });
      void alert({ title: t('settings.reindex_done', { defaultValue: 'Re-queued {{count}} document(s) for indexing.', count: r.queued }) as string });
    },
  });

  const purge = useMutation({
    mutationFn: () => documentsApi.purgeWorkspace(wsId!),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['documents', wsId] });
      void alert({ title: t('settings.purge_done', { defaultValue: 'Deleted {{count}} document(s).', count: r.deleted }) as string });
    },
  });
  async function confirmPurge() {
    const ok = await confirm({
      title: t('settings.purge_title', { defaultValue: 'Purge all documents?' }) as string,
      description: t('settings.purge_prompt', { defaultValue: 'This deletes EVERY document in this workspace (and its vectors, index, and files). Type PURGE to confirm.' }) as string,
      requireText: 'PURGE',
      confirmLabel: t('settings.purge_cta', { defaultValue: 'Purge everything' }) as string,
    });
    if (ok) purge.mutate();
  }

  return (
    <AppShell
      title={t('settings.title', { defaultValue: 'Settings' })}
      description={workspace?.name}
      maxWidth="max-w-4xl"
    >
      <AdminSiblingNav wsId={wsId!} current="settings" />
      <div className="space-y-6">
        <section className="card-paper p-6">
          <h2 className="display text-[16px] font-semibold text-ink-900">{t('settings.ai_section')}</h2>
          <p className="text-[13px] text-ink-600 mt-1">{t('settings.ai_intro')}</p>
          <dl className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold">{t('settings.spent_mtd')}</dt>
              <dd className="num text-2xl font-semibold text-ink-900 mt-1">${((usage?.monthCents ?? 0) / 100).toFixed(2)}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold">{t('settings.cap')}</dt>
              <dd className="num text-2xl font-semibold text-ink-900 mt-1">
                {usage?.capCents == null ? '—' : `$${(usage.capCents / 100).toFixed(0)}`}
              </dd>
              <p className="text-xs text-ink-500 mt-0.5">{t('settings.cap_help')}</p>
            </div>
            <div className="flex items-end gap-2">
              <input
                value={capUsd}
                onChange={(e) => setCapUsd(e.target.value)}
                placeholder={t('settings.cap_placeholder') as string}
                inputMode="decimal"
                className="num w-32 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
              />
              <button
                onClick={saveCap}
                disabled={update.isPending || !company}
                className="btn-press px-3 py-2 text-xs"
              >
                {update.isPending ? t('settings.saving') : t('settings.edit_cap')}
              </button>
            </div>
          </dl>

          {usage && usage.byModel.length > 0 && (
            <div className="mt-7">
              <h3 className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-2">
                {t('settings.spend_by_model')}
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-500 border-b border-ink-100">
                    <th className="py-2 pr-3 font-medium">{t('common.model')}</th>
                    <th className="py-2 px-3 font-medium text-right">{t('settings.th_runs')}</th>
                    <th className="py-2 px-3 font-medium text-right">{t('settings.th_cents')}</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.byModel.map((m) => (
                    <tr key={m.model} className="border-b border-ink-50">
                      <td className="py-2 pr-3 text-ink-900">{modelTierLabel(m.model)}</td>
                      <td className="py-2 px-3 num text-right">{m.runs}</td>
                      <td className="py-2 px-3 num text-right">${(m.cents / 100).toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <ThemeSection />
        <JpModeSection />

        <section className="card-paper p-6">
          {billingBanner === 'success' && (
            <div role="status" className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800 flex items-center justify-between gap-2">
              <span>{t('settings.billing_success', { defaultValue: 'Subscription updated. It can take a moment for the new plan to reflect — refresh if needed.' })}</span>
              <button type="button" onClick={() => setBillingBanner(null)} className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold">×</button>
            </div>
          )}
          {billingBanner === 'cancelled' && (
            <div role="status" className="mb-4 rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[13px] text-ink-700 flex items-center justify-between gap-2">
              <span>{t('settings.billing_cancelled', { defaultValue: 'Checkout cancelled. No changes were made.' })}</span>
              <button type="button" onClick={() => setBillingBanner(null)} className="text-ink-500 hover:text-ink-900 text-xs font-semibold">×</button>
            </div>
          )}
          <PlanPicker
            currentPlan={(company?.plan ?? 'free') as 'free' | 'growth' | 'global' | 'enterprise'}
            companyId={workspace?.companyId ?? ''}
            error={checkoutError}
            setError={setCheckoutError}
          />
        </section>

        <section className="card-paper p-6">
          <h2 className="display text-[16px] font-semibold text-ink-900">
            {t('settings.index', { defaultValue: 'Search index' })}
          </h2>
          {indexStats.data && (
            <dl className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
              {([
                ['documents', t('settings.idx_docs', { defaultValue: 'Documents' })],
                ['indexed', t('settings.idx_indexed', { defaultValue: 'Indexed' })],
                ['failed', t('settings.idx_failed', { defaultValue: 'Failed' })],
                ['chunks', t('settings.idx_chunks', { defaultValue: 'Chunks' })],
                ['vectors', t('settings.idx_vectors', { defaultValue: 'Vectors' })],
              ] as const).map(([k, label]) => (
                <div key={k} className="rounded-lg bg-paper-100 px-3 py-2">
                  <dt className="text-[11px] uppercase tracking-[0.12em] text-ink-500">{label}</dt>
                  <dd className="num text-lg font-semibold text-ink-900">{(indexStats.data as Record<string, number>)[k].toLocaleString()}</dd>
                </div>
              ))}
            </dl>
          )}
          <p className="mt-3 text-xs text-ink-500">
            {t('settings.reindex_help', { defaultValue: 'Re-run ingestion for every document — use after changing the embedding model or rebuilding the vector store. Owners/admins only.' })}
          </p>
          <button
            onClick={async () => {
              const ok = await confirm({
                title: t('settings.reindex_confirm', { defaultValue: 'Re-index every document in this workspace?' }) as string,
                confirmLabel: t('settings.reindex_all', { defaultValue: 'Re-index all documents' }) as string,
                destructive: false,
              });
              if (ok) reindexAll.mutate();
            }}
            disabled={reindexAll.isPending}
            className="mt-2 btn-ghost px-4 py-2 text-sm disabled:opacity-50"
          >
            {reindexAll.isPending ? t('common.loading', { defaultValue: 'Working…' }) : t('settings.reindex_all', { defaultValue: 'Re-index all documents' })}
          </button>
        </section>

        <section className="card-paper p-6 border border-rose-200">
          <h2 className="display text-[16px] font-semibold text-rose-700">
            {t('settings.danger_zone', { defaultValue: 'Danger zone' })}
          </h2>
          <p className="mt-1 text-[13px] text-ink-500">
            {t('settings.purge_help', { defaultValue: 'Permanently delete every document in this workspace — including its vectors, lexical index, and stored files. Owners and admins only. This cannot be undone.' })}
          </p>
          <button
            onClick={confirmPurge}
            disabled={purge.isPending}
            className="mt-3 px-4 py-2 text-sm rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          >
            {purge.isPending
              ? t('common.loading', { defaultValue: 'Working…' })
              : t('settings.purge_button', { defaultValue: 'Purge all documents' })}
          </button>
        </section>
      </div>
    </AppShell>
  );
}

function ThemeSection() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const options: { value: Theme; label: string; hint: string }[] = [
    { value: 'system', label: t('settings.theme_system', { defaultValue: 'System' }) as string, hint: t('settings.theme_system_hint', { defaultValue: 'Follow OS preference' }) as string },
    { value: 'light', label: t('settings.theme_light', { defaultValue: 'Light' }) as string, hint: t('settings.theme_light_hint', { defaultValue: 'Warm paper-archive' }) as string },
    { value: 'dark', label: t('settings.theme_dark', { defaultValue: 'Dark' }) as string, hint: t('settings.theme_dark_hint', { defaultValue: 'AAA-contrast night mode' }) as string },
  ];
  return (
    <section className="card-paper p-6">
      <h2 className="display text-[16px] font-semibold text-ink-900">
        {t('settings.theme_section', { defaultValue: 'Appearance' })}
      </h2>
      <p className="text-[13px] text-ink-600 mt-1">
        {t('settings.theme_intro', { defaultValue: 'Pick a theme. Synced across tabs; preference lives in this browser.' })}
      </p>
      <div className="mt-4 inline-flex rounded-lg border border-ink-200 bg-paper-50 p-1">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => setTheme(o.value)}
            className={`px-4 py-2 rounded-md text-[13px] font-medium transition ${
              theme === o.value
                ? 'bg-white text-ink-900 shadow-press'
                : 'text-ink-600 hover:text-ink-900'
            }`}
            title={o.hint}
          >
            {o.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function JpModeSection() {
  const { t } = useTranslation();
  const jp = useJpMode();
  return (
    <section className="card-paper p-6">
      <h2 className="display text-[16px] font-semibold text-ink-900">
        {t('settings.jp_section', { defaultValue: 'Japanese display' })}
      </h2>
      <p className="text-[13px] text-ink-600 mt-1">
        {t('settings.jp_intro', {
          defaultValue: 'Switch dates to 元号, numbers to 千 / 百万 / 億, or full-width digits — anywhere those values render in the app.',
        })}
      </p>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={jp.wareki}
            onChange={(e) => jp.setWareki(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-ink-300 text-accent-500 focus:ring-accent-500/30"
          />
          <div>
            <div className="text-[13.5px] font-semibold text-ink-900">{t('settings.jp_wareki', { defaultValue: '元号 (Japanese era) dates' })}</div>
            <div className="text-[12px] text-ink-500">2025 → 令和7年</div>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={jp.furigana}
            onChange={(e) => jp.setFurigana(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-ink-300 text-accent-500 focus:ring-accent-500/30"
          />
          <div>
            <div className="text-[13.5px] font-semibold text-ink-900">{t('settings.jp_furigana', { defaultValue: 'Furigana (ふりがな)' })}</div>
            <div className="text-[12px] text-ink-500">{t('settings.jp_furigana_help', { defaultValue: 'Show reading hints above kanji when available.' })}</div>
          </div>
        </label>

        <div className="block">
          <span className="block text-[11px] uppercase tracking-[0.12em] text-ink-500 mb-1 font-semibold">
            {t('settings.jp_unit', { defaultValue: 'Numeric display' })}
          </span>
          <div className="w-full">
            <Dropdown<NumericUnit>
              value={jp.numericUnit}
              onChange={(next) => jp.setNumericUnit(next)}
              items={[
                { value: 'plain', label: '1,234,567' },
                { value: 'unit', label: t('settings.jp_unit_compact', { defaultValue: '1.23百万 (千 / 百万 / 億)' }) as string },
                { value: 'wide', label: `１,２３４,５６７ ${t('settings.jp_unit_wide_suffix', { defaultValue: '(full-width digits)' }) as string}` },
              ]}
              ariaLabel={t('settings.jp_unit', { defaultValue: 'Numeric display' }) as string}
              size="md"
              className="w-full"
            />
          </div>
        </div>

        <div className="block">
          <span className="block text-[11px] uppercase tracking-[0.12em] text-ink-500 mb-1 font-semibold">
            {t('settings.jp_keigo', { defaultValue: 'Default keigo register (Compose)' })}
          </span>
          <div className="w-full">
            <Dropdown<KeigoRegister>
              value={jp.keigoRegister}
              onChange={(next) => jp.setKeigoRegister(next)}
              items={[
                { value: 'formal-first', label: t('compose.register.formal_first', { defaultValue: '初対面・丁寧 (formal · first contact)' }) as string },
                { value: 'formal-followup', label: t('compose.register.formal_followup', { defaultValue: '二回目以降 (formal · followup)' }) as string },
                { value: 'introduced', label: t('compose.register.introduced', { defaultValue: '紹介経由 (via introduction)' }) as string },
                { value: 'friendly', label: t('compose.register.friendly', { defaultValue: '友好的 (friendly)' }) as string },
              ]}
              ariaLabel={t('settings.jp_keigo', { defaultValue: 'Default keigo register (Compose)' }) as string}
              size="md"
              className="w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * In-app plan picker. Mirrors the public pricing page: all four tiers
 * in one view (Free + 3 paid), monthly/yearly toggle, current-plan
 * badge on the active tier, per-tier Subscribe/Switch CTAs calling
 * /api/billing/checkout. Free is informational only.
 */
function PlanPicker({
  currentPlan,
  companyId,
  error,
  setError,
}: {
  currentPlan: 'free' | 'growth' | 'global' | 'enterprise';
  companyId: string;
  error: string | null;
  setError: (s: string | null) => void;
}) {
  const { t } = useTranslation();
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [pending, setPending] = useState<'growth' | 'global' | 'enterprise' | null>(null);

  // Live Stripe-sourced plan catalogue. Never hardcode prices here —
  // a Stripe Dashboard edit would silently desync the picker from
  // what Checkout actually charges.
  const plansQ = useQuery({ queryKey: ['billing-plans'], queryFn: () => billingApi.plans() });
  const plans = plansQ.data ?? [];
  const priceFor = (code: 'free' | 'growth' | 'global' | 'enterprise'): number => {
    const p = plans.find((x) => x.code === code);
    if (!p) return 0;
    const cents = interval === 'monthly' ? p.monthlyUnitAmountCents : p.yearlyUnitAmountCents;
    // Yearly cents = full annual price; the card shows effective monthly.
    return interval === 'yearly' ? Math.round(cents / 12) / 100 : cents / 100;
  };
  const currency = (plans[0]?.currency ?? 'usd').toUpperCase();
  const fmt = (n: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);

  const tiers: Array<{
    code: 'free' | 'growth' | 'global' | 'enterprise';
    name: string;
    perks: string[];
    highlight: boolean;
  }> = [
    { code: 'free',       name: t('pricing.free',       { defaultValue: 'Free' }),       perks: ['10 seats · 5 connectors · 100K chunks'],                                       highlight: false },
    { code: 'growth',     name: t('pricing.growth',     { defaultValue: 'Growth' }),     perks: ['Unlimited connectors · 5M chunks · included AI cap'],                          highlight: true  },
    { code: 'global',     name: t('pricing.global',     { defaultValue: 'Global' }),     perks: ['Multi-region · knowledge graph · signals'],                                    highlight: false },
    { code: 'enterprise', name: t('pricing.enterprise', { defaultValue: 'Enterprise' }), perks: ['SSO/SAML/SCIM · on-prem option · 7y retention · 99.95% SLA'],                  highlight: false },
  ];

  // Manage subscription button only shows after the first checkout —
  // /billing/me returns hasStripeCustomer=true once a Stripe Customer
  // exists for the workspace.
  const billingMe = useQuery<BillingMe>({
    queryKey: ['billing-me', companyId],
    enabled: !!companyId,
    queryFn: async () => billingApi.me(companyId),
  });
  const canManage = billingMe.data?.hasStripeCustomer === true;

  // Preselect the cycle the user is actually subscribed to (once), instead of
  // always defaulting to monthly.
  const cycleInited = useRef(false);
  useEffect(() => {
    const bi = billingMe.data?.billingInterval;
    if (!cycleInited.current && bi) {
      setInterval(bi);
      cycleInited.current = true;
    }
  }, [billingMe.data?.billingInterval]);

  async function subscribe(plan: 'growth' | 'global' | 'enterprise') {
    if (!companyId) return;
    setError(null);
    setPending(plan);
    try {
      const { url } = await billingApi.checkout({ plan, interval, seats: 1, companyId });
      window.location.href = url;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 503 ? t('settings.upgrade_unavailable', { defaultValue: 'Billing not yet enabled.' }) : (err as Error).message);
      setPending(null);
    }
  }

  async function openPortal() {
    if (!companyId) return;
    setError(null);
    try {
      const { url } = await billingApi.portal(companyId, `${window.location.origin}/app/settings`);
      window.location.href = url;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 503 ? t('settings.portal_unavailable', { defaultValue: 'Subscribe first to manage your billing.' }) : (err as Error).message);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="display text-[16px] font-semibold text-ink-900">{t('settings.plan_section')}</h2>
          <p className="text-[13px] text-ink-600 mt-1">{t('settings.plan_intro')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && (
            <button
              type="button"
              onClick={openPortal}
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 font-semibold"
            >
              {t('settings.manage_subscription', { defaultValue: 'Manage subscription' })}
            </button>
          )}
          <div className="inline-flex rounded-lg border border-ink-200 p-0.5 bg-ink-50 text-xs font-semibold">
            {(['monthly', 'yearly'] as const).map((iv) => (
              <button
                key={iv}
                type="button"
                onClick={() => setInterval(iv)}
                className={'px-3 py-1.5 rounded-md transition ' + (interval === iv ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900')}
              >
                {iv === 'monthly' ? 'Monthly' : 'Yearly · −17%'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {canManage && billingMe.data?.currentPeriodEnd && (
        <p className="mt-2 text-[12px] text-ink-500">
          {billingMe.data.cancelAtPeriodEnd
            ? t('settings.cancels_on', { defaultValue: 'Cancels on {{date}}', date: new Date(billingMe.data.currentPeriodEnd).toLocaleDateString() })
            : t('settings.renews_on', { defaultValue: 'Renews on {{date}}', date: new Date(billingMe.data.currentPeriodEnd).toLocaleDateString() })}
        </p>
      )}

      {error && (
        <div className="mt-4">
          <InlineAlert tone="error" onDismiss={() => setError(null)}>{error}</InlineAlert>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {tiers.map((tier) => {
          const isCurrent = tier.code === currentPlan;
          const price = priceFor(tier.code);
          return (
            <div
              key={tier.code}
              className={
                'rounded-xl border p-4 flex flex-col ' +
                (isCurrent
                  ? 'border-accent-500 bg-accent-50/40 ring-1 ring-accent-500/30'
                  : 'border-ink-200 bg-white')
              }
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink-900">{tier.name}</h3>
                {isCurrent && <span className="text-[10px] uppercase tracking-wide font-bold text-accent-700">{t('settings.plan_current', { defaultValue: 'Current' })}</span>}
              </div>
              <p className="mt-2 num text-2xl font-bold text-ink-900">
                {plansQ.isLoading ? '—' : fmt(price)}
              </p>
              <p className="text-xs text-ink-500">{tier.code === 'free' ? 'Forever free' : `/ seat / ${interval === 'monthly' ? 'month' : 'month, billed yearly'}`}</p>
              <ul className="mt-3 space-y-1 text-xs text-ink-600 flex-1">
                {tier.perks.map((p) => (
                  <li key={p} className="flex gap-1.5"><span className="text-accent-500 font-bold">✓</span><span>{p}</span></li>
                ))}
              </ul>
              {tier.code !== 'free' && (
                <button
                  type="button"
                  disabled={isCurrent || pending !== null}
                  onClick={() => subscribe(tier.code as 'growth' | 'global' | 'enterprise')}
                  className={
                    'mt-4 w-full rounded-md px-3 py-2 text-xs font-semibold transition ' +
                    (isCurrent
                      ? 'bg-ink-100 text-ink-500 cursor-default'
                      : 'bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-60')
                  }
                >
                  {isCurrent
                    ? 'Current plan'
                    : pending === tier.code
                      ? 'Opening…'
                      : currentPlan === 'free' ? 'Subscribe' : 'Switch'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 text-[11px] text-ink-500">
        <Link to="/pricing" className="underline-offset-2 hover:underline">
          {t('settings.see_plans')}
        </Link>
      </div>
    </>
  );
}
